// A pattern's history, drawn.
//
// Two quantities, deliberately drawn differently, because they are different kinds of thing. The
// bars are sightings in a month: discrete events, three separate entries in June. The line is the
// 60-day rolling count, which slides every day and is the number promotion actually judges. Drawing
// both as a curve would imply the entries flow into one another; drawing both as bars would hide
// that the threshold is crossed by the rolling total, not by any single month.
//
// Drawing it this way makes promotion explainable rather than asserted: the month a symbol earned a
// reading is the month the line crossed the bar, and both are on screen.

import { useId, useMemo } from 'react';
import { smoothPath } from '../components/SelfStats';
import type { PatternTimeline } from '../api/pattern-timeline';

const W = 640;
const H = 190;
const PAD_X = 26;
const PAD_TOP = 22;
const PAD_BOT = 30;

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** "2026-06" → "Jun". Returns the key unchanged if it is not the shape we expect. */
export function monthLabel(key: string): string {
  const [year, month] = key.split('-');
  const index = Number(month) - 1;
  if (!year || Number.isNaN(index) || index < 0 || index > 11) return key;
  return MONTHS[index];
}

/**
 * Which months get a label, so the axis stays readable at any span.
 *
 * Always the first and last, then an even spread between. A year of monthly labels overlaps into
 * illegibility at phone width, and dropping the last one would leave the chart apparently ending
 * at some arbitrary earlier month.
 */
export function labelledMonths(count: number, max = 6): number[] {
  if (count <= 0) return [];
  if (count <= max) return Array.from({ length: count }, (_, i) => i);
  const step = (count - 1) / (max - 1);
  const picked = new Set<number>();
  for (let i = 0; i < max; i++) picked.add(Math.round(i * step));
  return [...picked].sort((a, b) => a - b);
}

/** The first month at which the pattern was no longer merely emerging, or -1 if it never was. */
export function promotionIndex(months: { significance: string }[]): number {
  return months.findIndex((m) => m.significance !== 'emerging');
}

export function PatternArc({ timeline, summary }: { timeline: PatternTimeline; summary: string }) {
  const uid = useId().replace(/:/g, '');
  const months = timeline.months;

  const geometry = useMemo(() => {
    if (months.length === 0) return null;

    // One scale for both series, since both are counts of the same thing. A second axis would let
    // the line and the bars be compared when they cannot be.
    const peak = Math.max(1, ...months.map((m) => Math.max(m.count, m.inWindow)));
    const plotW = W - PAD_X * 2;
    const plotH = H - PAD_TOP - PAD_BOT;
    const slot = plotW / months.length;
    const barW = Math.max(3, Math.min(22, slot * 0.42));
    const y = (value: number) => PAD_TOP + (1 - value / peak) * plotH;
    const cx = (i: number) => PAD_X + slot * i + slot / 2;

    return {
      peak,
      slot,
      barW,
      baseline: PAD_TOP + plotH,
      bars: months.map((m, i) => ({ x: cx(i) - barW / 2, y: y(m.count), h: Math.max(0, PAD_TOP + plotH - y(m.count)), month: m })),
      // Gently smoothed. Catmull-Rom overshoots at sharp turns, and a rolling count that dipped
      // below the axis or rose above its own peak would be drawing a month that never happened —
      // so the curve is kept close enough to the points that it stays inside them.
      line: smoothPath(months.map((m, i) => ({ x: cx(i), y: y(m.inWindow) })), 0.1),
      cx,
    };
  }, [months]);

  if (!geometry) return null;

  const promoted = promotionIndex(months);
  const labels = labelledMonths(months.length);

  return (
    <figure className="oarc">
      <svg
        className="oarc-svg"
        viewBox={`0 0 ${W} ${H}`}
        preserveAspectRatio="none"
        role="img"
        aria-label={`${summary} Seen ${timeline.total} times across ${months.length} months.`}
      >
        <defs>
          <linearGradient id={`arcBar${uid}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="var(--accent-1)" stopOpacity="0.85" />
            <stop offset="100%" stopColor="var(--accent-1)" stopOpacity="0.28" />
          </linearGradient>
          {/* A backstop under the lowered tension, so no dataset can push the curve out of the
              plot no matter how sharply it turns. */}
          <clipPath id={`arcClip${uid}`}>
            <rect x={0} y={PAD_TOP - 4} width={W} height={H - PAD_TOP - PAD_BOT + 8} />
          </clipPath>
        </defs>

        {/* The month a reading was earned, marked where it happened rather than stated as a date
            elsewhere. Drawn under the data so it never obscures it. */}
        {promoted >= 0 ? (
          <g className="oarc-promo">
            <line x1={geometry.cx(promoted)} y1={PAD_TOP - 8} x2={geometry.cx(promoted)} y2={geometry.baseline} />
            <text x={geometry.cx(promoted) + 5} y={PAD_TOP - 11}>became a pattern</text>
          </g>
        ) : null}

        <line className="oarc-base" x1={PAD_X} y1={geometry.baseline} x2={W - PAD_X} y2={geometry.baseline} />

        {geometry.bars.map((bar) => (
          <rect
            key={bar.month.month}
            className="oarc-bar"
            x={bar.x}
            y={bar.y}
            width={geometry.barW}
            height={bar.h}
            rx={Math.min(3, geometry.barW / 2)}
            fill={`url(#arcBar${uid})`}
          />
        ))}

        <path className="oarc-line" d={geometry.line} clipPath={`url(#arcClip${uid})`} />

        {labels.map((i) => (
          <text key={i} className="oarc-tick" x={geometry.cx(i)} y={H - 9} textAnchor="middle">
            {monthLabel(months[i].month)}
          </text>
        ))}
      </svg>

      <figcaption className="oarc-key">
        <span className="oarc-key-bar" aria-hidden /> written that month
        <span className="oarc-key-line" aria-hidden /> counting toward a reading
      </figcaption>
    </figure>
  );
}
